import React, { useState, useEffect, useCallback } from 'react';
import FilterBar from './FilterBar';
import AppPageHeader from './AppPageHeader';
import { comparableScheduleDateKey } from './sheetDateFormat';

const ReportPage = ({ scheduleData, onBack, onRefetch }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    teacher: [],
    subject: [],
    class: [],
    projectName: [],
    year: '',
    startDate: '',
    endDate: ''
  });
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);

  // Apply filters logic (same as App.js)
  const applyFilters = useCallback(() => {
    let filtered = [...scheduleData];
    
    if (filters.teacher && filters.teacher.length > 0) {
      filtered = filtered.filter(item => filters.teacher.includes(item.teacher));
    }
    if (filters.subject && filters.subject.length > 0) {
      filtered = filtered.filter(item => filters.subject.includes(item.subject));
    }
    if (filters.class && filters.class.length > 0) {
      filtered = filtered.filter(item => filters.class.includes(item.class));
    }
    if (filters.projectName && filters.projectName.length > 0) {
      filtered = filtered.filter(item => filters.projectName.includes(item.projectName));
    }
    if (filters.year) {
      filtered = filtered.filter(item => {
        const key = comparableScheduleDateKey(item.dateISO);
        if (!key) return false;
        return key.slice(0, 4) === filters.year;
      });
    }
    if (filters.startDate) {
      filtered = filtered.filter(item => {
        const key = comparableScheduleDateKey(item.dateISO);
        return key && key >= filters.startDate;
      });
    }
    if (filters.endDate) {
      filtered = filtered.filter(item => {
        const key = comparableScheduleDateKey(item.dateISO);
        return key && key <= filters.endDate;
      });
    }

    setFilteredData(filtered);
  }, [scheduleData, filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    if (filterName === 'startDate' || filterName === 'endDate') {
      setActiveQuickFilter(null);
    }
  };

  const handleDateRangeCommit = useCallback(({ startDate, endDate, activeQuickFilter: qf }) => {
    setFilters(prev => ({ ...prev, startDate, endDate }));
    setActiveQuickFilter(qf ?? null);
  }, []);

  const handleReset = () => {
    setFilters({
      teacher: [],
      subject: [],
      class: [],
      projectName: [],
      year: '',
      startDate: '',
      endDate: ''
    });
    setActiveQuickFilter(null);
  };

  // Calculate statistics
  const calculateStats = (data) => {
    // Filter out N/A entries
    const validData = data.filter(item => 
      item.teacher && item.teacher !== 'N/A' && 
      item.date && item.date !== 'N/A'
    );

    // Total classes
    const totalClasses = validData.length;

    // Classes per teacher
    const teacherStats = {};
    validData.forEach(item => {
      if (item.teacher) {
        teacherStats[item.teacher] = (teacherStats[item.teacher] || 0) + 1;
      }
    });

    // Classes per class and subject
    const classSubjectStats = {};
    validData.forEach(item => {
      if (item.class && item.subject) {
        if (!classSubjectStats[item.class]) {
          classSubjectStats[item.class] = {};
        }
        classSubjectStats[item.class][item.subject] = 
          (classSubjectStats[item.class][item.subject] || 0) + 1;
      }
    });

    return {
      totalClasses,
      teacherStats,
      classSubjectStats
    };
  };

  // Get all stats (unfiltered)
  const allStats = calculateStats(scheduleData);
  
  // Get filtered stats
  const filteredStats = calculateStats(filteredData);

  // Define expected subjects for each class
  const expectedSubjects = {
    'Class 6': ['Bangla 1st', 'Bangla 2nd', 'English 1st', 'English 2nd', 'General Math', 'General Science', 'BGS', 'ICT'],
    'Class 7': ['Bangla 1st', 'Bangla 2nd', 'English 1st', 'English 2nd', 'General Math', 'General Science', 'BGS', 'ICT'],
    'Class 8': ['Bangla 1st', 'Bangla 2nd', 'English 1st', 'English 2nd', 'General Math', 'General Science', 'BGS', 'ICT'],
    'Class 9': ['Bangla 1st', 'Bangla 2nd', 'English 1st', 'English 2nd', 'General Math', 'General Science', 'BGS', 'ICT', 'Physics', 'Chemistry', 'Biology', 'Higher Math'],
    'Class 10': ['Bangla 1st', 'Bangla 2nd', 'English 1st', 'English 2nd', 'General Math', 'General Science', 'BGS', 'ICT', 'Physics', 'Chemistry', 'Biology', 'Higher Math']
  };

  // Get all unique classes from data
  const allClasses = Array.from(new Set(scheduleData.map(item => item.class).filter(Boolean))).sort();

  const renderClassSubjectStats = (stats) => {
    return allClasses.map(className => {
      const classTotal = Object.values(stats.classSubjectStats[className] || {}).reduce((sum, count) => sum + count, 0);
      const subjects = stats.classSubjectStats[className] || {};
      
      // Get all subjects for this class from the data
      const allSubjectsForClass = Array.from(new Set(
        scheduleData
          .filter(item => item.class === className && item.subject)
          .map(item => item.subject)
      )).sort();
      
      // Combine expected subjects with actual subjects from data
      const expectedSubjectsForClass = expectedSubjects[className] || [];
      const combinedSubjects = [...new Set([...expectedSubjectsForClass, ...allSubjectsForClass])].sort();
      
      return (
        <div key={className} className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            {className} - <span className="text-blue-600">{classTotal} Total Classes</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {combinedSubjects.map(subject => {
              const count = subjects[subject] || 0;
              return (
                <div key={subject} className="bg-gray-50 rounded-lg p-3 border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">{subject}</span>
                    <span className="text-lg font-bold text-blue-600">{count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  };

  const renderTeacherStats = (stats) => {
    const sortedTeachers = Object.entries(stats.teacherStats).sort((a, b) => b[1] - a[1]);
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedTeachers.map(([teacher, count]) => (
          <div key={teacher} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">{teacher}</span>
              <span className="text-xl font-bold text-blue-600">{count}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppPageHeader
        onBack={onBack}
        title="AFS Class Schedule Report"
        backAriaLabel="Back to live class schedule"
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <FilterBar 
          filters={filters}
          onFilterChange={handleFilterChange}
          onDateRangeCommit={handleDateRangeCommit}
          onReset={handleReset}
          onRefetch={onRefetch || (() => {})}
          data={scheduleData}
          filteredData={filteredData}
          activeQuickFilter={activeQuickFilter}
          setActiveQuickFilter={setActiveQuickFilter}
          isReportPage={true}
        />

        {/* Statistics Sections */}
        <div className="space-y-8">
          {/* Total Classes Summary */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Total Classes Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{allStats.totalClasses}</div>
                <div className="text-lg text-gray-700">Total Classes (All Data)</div>
              </div>
              <div className="bg-green-50 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">{filteredStats.totalClasses}</div>
                <div className="text-lg text-gray-700">Filtered Classes</div>
              </div>
            </div>
          </div>

          {/* Classes Per Teacher */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Classes Per Teacher</h2>
            {Object.keys(filteredStats.teacherStats).length > 0 ? (
              renderTeacherStats(filteredStats)
            ) : (
              <div className="text-center text-gray-500 py-8">
                No teacher data available with current filters
              </div>
            )}
          </div>

          {/* Classes Per Class and Subject */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Classes Per Class and Subject</h2>
            {Object.keys(filteredStats.classSubjectStats).length > 0 ? (
              renderClassSubjectStats(filteredStats)
            ) : (
              <div className="text-center text-gray-500 py-8">
                No class/subject data available with current filters
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReportPage;
